import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FolderKanban,
  Briefcase,
  Users,
  MessageSquare,
  LogOut,
  Plus,
  Pencil,
  Trash2,
  X,
  Save,
  Image,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("projects");
  const [data, setData] = useState({
    projects: [],
    services: [],
    team: [],
    contacts: [],
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create"); // create or edit
  const [editingItem, setEditingItem] = useState(null);
  const [adminInfo, setAdminInfo] = useState(null);

  const getAuthHeaders = () => ({
    headers: {
      Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
    },
  });

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    const info = localStorage.getItem("admin_info");
    
    if (!token) {
      navigate("/admin");
      return;
    }
    
    if (info) {
      setAdminInfo(JSON.parse(info));
    }
    
    fetchAllData();
  }, [navigate]);

  const fetchAllData = async () => {
    try {
      const [projectsRes, servicesRes, teamRes, contactsRes] = await Promise.all([
        axios.get(`${API}/projects`),
        axios.get(`${API}/services`),
        axios.get(`${API}/team`),
        axios.get(`${API}/admin/contacts`, getAuthHeaders()),
      ]);
      setData({
        projects: projectsRes.data,
        services: servicesRes.data,
        team: teamRes.data,
        contacts: contactsRes.data,
      });
    } catch (error) {
      if (error.response?.status === 401) {
        handleLogout();
      }
      console.error("Error fetching data:", error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_info");
    navigate("/admin");
    toast.success("Logged out successfully");
  };

  const handleCreate = () => {
    setModalMode("create");
    setEditingItem(getEmptyItem());
    setIsModalOpen(true);
  };

  const handleEdit = (item) => {
    setModalMode("edit");
    setEditingItem({ ...item });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this item?")) return;

    try {
      const endpoint = activeTab === "team" ? "team" : activeTab;
      await axios.delete(`${API}/admin/${endpoint}/${id}`, getAuthHeaders());
      toast.success("Item deleted successfully");
      fetchAllData();
    } catch (error) {
      toast.error("Failed to delete item");
    }
  };

  const handleSave = async () => {
    try {
      const endpoint = activeTab === "team" ? "team" : activeTab;
      
      if (modalMode === "create") {
        await axios.post(`${API}/admin/${endpoint}`, editingItem, getAuthHeaders());
        toast.success("Item created successfully");
      } else {
        await axios.put(`${API}/admin/${endpoint}/${editingItem.id}`, editingItem, getAuthHeaders());
        toast.success("Item updated successfully");
      }
      
      setIsModalOpen(false);
      fetchAllData();
    } catch (error) {
      toast.error("Failed to save item");
    }
  };

  const getEmptyItem = () => {
    switch (activeTab) {
      case "projects":
        return {
          title: "",
          category: "residential",
          description: "",
          location: "",
          year: new Date().getFullYear().toString(),
          image_url: "",
          featured: false,
        };
      case "services":
        return {
          title: "",
          description: "",
          icon: "building",
          features: [],
        };
      case "team":
        return {
          name: "",
          role: "",
          bio: "",
          image_url: "",
          email: "",
        };
      default:
        return {};
    }
  };

  const tabs = [
    { id: "projects", label: "Projects", icon: FolderKanban },
    { id: "services", label: "Services", icon: Briefcase },
    { id: "team", label: "Team", icon: Users },
    { id: "contacts", label: "Inquiries", icon: MessageSquare },
  ];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#F4F4F0]">
      {/* Header */}
      <header className="bg-[#1A1A1A] text-[#F4F4F0] px-6 md:px-12 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img
              src="https://customer-assets.emergentagent.com/job_design-farmhouse/artifacts/5ma1d1zg_logo.PNG"
              alt="IIDAS"
              className="h-10 w-auto"
            />
            <span className="text-sm text-[#F4F4F0]/70">Admin Dashboard</span>
          </div>
          <div className="flex items-center gap-4">
            {adminInfo && (
              <span className="text-sm text-[#F4F4F0]/70 hidden md:block">
                {adminInfo.name}
              </span>
            )}
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="flex items-center gap-2 text-sm text-[#F4F4F0]/70 hover:text-[#F4F4F0] transition-colors"
            >
              <LogOut size={18} />
              <span className="hidden md:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <div className="px-6 md:px-12 py-8">
        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8 border-b border-[#1A1A1A]/10 pb-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
              className={`flex items-center gap-2 px-4 py-2 text-sm transition-colors ${
                activeTab === tab.id
                  ? "bg-[#1A1A1A] text-[#F4F4F0]"
                  : "text-[#1A1A1A]/70 hover:bg-[#E5E5E0]"
              }`}
            >
              <tab.icon size={16} />
              {tab.label}
              <span className="ml-1 text-xs opacity-60">
                ({data[tab.id]?.length || 0})
              </span>
            </button>
          ))}
        </div>

        {/* Action Bar */}
        {activeTab !== "contacts" && (
          <div className="flex justify-end mb-6">
            <button
              onClick={handleCreate}
              data-testid="add-new-btn"
              className="flex items-center gap-2 bg-[#3A4D39] text-[#F4F4F0] px-4 py-2 text-sm hover:bg-[#1A1A1A] transition-colors"
            >
              <Plus size={16} />
              Add New
            </button>
          </div>
        )}

        {/* Content */}
        <div className="bg-white border border-[#1A1A1A]/10">
          {/* Projects */}
          {activeTab === "projects" && (
            <div className="divide-y divide-[#1A1A1A]/10">
              {data.projects.map((project) => (
                <div
                  key={project.id}
                  className="flex items-center gap-4 p-4 hover:bg-[#F4F4F0] transition-colors"
                  data-testid={`project-row-${project.id}`}
                >
                  <img
                    src={project.image_url}
                    alt={project.title}
                    className="w-16 h-16 object-cover flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-[#1A1A1A] truncate">
                      {project.title}
                    </h3>
                    <p className="text-sm text-[#1A1A1A]/60">
                      {project.category} • {project.location} • {project.year}
                    </p>
                  </div>
                  {project.featured && (
                    <span className="text-xs bg-[#3A4D39] text-[#F4F4F0] px-2 py-1">
                      Featured
                    </span>
                  )}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEdit(project)}
                      className="p-2 text-[#1A1A1A]/60 hover:text-[#3A4D39]"
                      data-testid={`edit-project-${project.id}`}
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(project.id)}
                      className="p-2 text-[#1A1A1A]/60 hover:text-red-600"
                      data-testid={`delete-project-${project.id}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
              {data.projects.length === 0 && (
                <p className="p-8 text-center text-[#1A1A1A]/50">No projects yet</p>
              )}
            </div>
          )}

          {/* Services */}
          {activeTab === "services" && (
            <div className="divide-y divide-[#1A1A1A]/10">
              {data.services.map((service) => (
                <div
                  key={service.id}
                  className="flex items-center gap-4 p-4 hover:bg-[#F4F4F0] transition-colors"
                  data-testid={`service-row-${service.id}`}
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-[#E5E5E0] flex-shrink-0">
                    <Briefcase size={20} className="text-[#3A4D39]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-[#1A1A1A]">{service.title}</h3>
                    <p className="text-sm text-[#1A1A1A]/60 truncate">
                      {service.description}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEdit(service)}
                      className="p-2 text-[#1A1A1A]/60 hover:text-[#3A4D39]"
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(service.id)}
                      className="p-2 text-[#1A1A1A]/60 hover:text-red-600"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
              {data.services.length === 0 && (
                <p className="p-8 text-center text-[#1A1A1A]/50">No services yet</p>
              )}
            </div>
          )}

          {/* Team */}
          {activeTab === "team" && (
            <div className="divide-y divide-[#1A1A1A]/10">
              {data.team.map((member) => (
                <div
                  key={member.id}
                  className="flex items-center gap-4 p-4 hover:bg-[#F4F4F0] transition-colors"
                  data-testid={`team-row-${member.id}`}
                >
                  <img
                    src={member.image_url}
                    alt={member.name}
                    className="w-12 h-12 object-cover rounded-full flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-[#1A1A1A]">{member.name}</h3>
                    <p className="text-sm text-[#1A1A1A]/60">{member.role}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEdit(member)}
                      className="p-2 text-[#1A1A1A]/60 hover:text-[#3A4D39]"
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(member.id)}
                      className="p-2 text-[#1A1A1A]/60 hover:text-red-600"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
              {data.team.length === 0 && (
                <p className="p-8 text-center text-[#1A1A1A]/50">No team members yet</p>
              )}
            </div>
          )}

          {/* Contacts */}
          {activeTab === "contacts" && (
            <div className="divide-y divide-[#1A1A1A]/10">
              {data.contacts.map((contact) => (
                <div
                  key={contact.id}
                  className="p-4 hover:bg-[#F4F4F0] transition-colors"
                  data-testid={`contact-row-${contact.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-medium text-[#1A1A1A]">{contact.name}</h3>
                        <span className="text-xs text-[#1A1A1A]/40">
                          {new Date(contact.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="text-sm text-[#3A4D39] mb-1">{contact.subject}</p>
                      <p className="text-sm text-[#1A1A1A]/60 mb-2">{contact.message}</p>
                      <div className="flex gap-4 text-xs text-[#1A1A1A]/50">
                        <span>{contact.email}</span>
                        {contact.phone && <span>{contact.phone}</span>}
                      </div>
                    </div>
                    <button
                      onClick={() => handleDelete(contact.id)}
                      className="p-2 text-[#1A1A1A]/60 hover:text-red-600"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
              {data.contacts.length === 0 && (
                <p className="p-8 text-center text-[#1A1A1A]/50">No inquiries yet</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#1A1A1A]/50"
            onClick={() => setIsModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-[#F4F4F0] w-full max-w-lg max-h-[90vh] overflow-auto"
              onClick={(e) => e.stopPropagation()}
              data-testid="edit-modal"
            >
              <div className="flex items-center justify-between p-4 border-b border-[#1A1A1A]/10">
                <h2 className="font-heading text-xl text-[#1A1A1A]">
                  {modalMode === "create" ? "Add New" : "Edit"}{" "}
                  {activeTab.slice(0, -1)}
                </h2>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="p-2 text-[#1A1A1A]/60 hover:text-[#1A1A1A]"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="p-6 space-y-4">
                {/* Project Form */}
                {activeTab === "projects" && editingItem && (
                  <>
                    <div>
                      <label className="block text-xs uppercase tracking-wider text-[#1A1A1A]/60 mb-2">
                        Title
                      </label>
                      <input
                        type="text"
                        value={editingItem.title}
                        onChange={(e) =>
                          setEditingItem({ ...editingItem, title: e.target.value })
                        }
                        className="w-full border border-[#1A1A1A]/20 px-3 py-2 focus:border-[#1A1A1A] focus:outline-none"
                        data-testid="modal-title-input"
                      />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-wider text-[#1A1A1A]/60 mb-2">
                        Category
                      </label>
                      <select
                        value={editingItem.category}
                        onChange={(e) =>
                          setEditingItem({ ...editingItem, category: e.target.value })
                        }
                        className="w-full border border-[#1A1A1A]/20 px-3 py-2 focus:border-[#1A1A1A] focus:outline-none"
                      >
                        <option value="residential">Residential</option>
                        <option value="commercial">Commercial</option>
                        <option value="interior">Interior</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-wider text-[#1A1A1A]/60 mb-2">
                        Description
                      </label>
                      <textarea
                        value={editingItem.description}
                        onChange={(e) =>
                          setEditingItem({ ...editingItem, description: e.target.value })
                        }
                        rows={3}
                        className="w-full border border-[#1A1A1A]/20 px-3 py-2 focus:border-[#1A1A1A] focus:outline-none resize-none"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs uppercase tracking-wider text-[#1A1A1A]/60 mb-2">
                          Location
                        </label>
                        <input
                          type="text"
                          value={editingItem.location}
                          onChange={(e) =>
                            setEditingItem({ ...editingItem, location: e.target.value })
                          }
                          className="w-full border border-[#1A1A1A]/20 px-3 py-2 focus:border-[#1A1A1A] focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-xs uppercase tracking-wider text-[#1A1A1A]/60 mb-2">
                          Year
                        </label>
                        <input
                          type="text"
                          value={editingItem.year}
                          onChange={(e) =>
                            setEditingItem({ ...editingItem, year: e.target.value })
                          }
                          className="w-full border border-[#1A1A1A]/20 px-3 py-2 focus:border-[#1A1A1A] focus:outline-none"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-wider text-[#1A1A1A]/60 mb-2">
                        Image URL
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={editingItem.image_url}
                          onChange={(e) =>
                            setEditingItem({ ...editingItem, image_url: e.target.value })
                          }
                          className="flex-1 border border-[#1A1A1A]/20 px-3 py-2 focus:border-[#1A1A1A] focus:outline-none"
                          placeholder="https://..."
                        />
                        <button className="p-2 border border-[#1A1A1A]/20">
                          <Image size={20} className="text-[#1A1A1A]/60" />
                        </button>
                      </div>
                      {editingItem.image_url && (
                        <img
                          src={editingItem.image_url}
                          alt="Preview"
                          className="mt-2 w-full h-32 object-cover"
                        />
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="featured"
                        checked={editingItem.featured}
                        onChange={(e) =>
                          setEditingItem({ ...editingItem, featured: e.target.checked })
                        }
                        className="w-4 h-4"
                      />
                      <label htmlFor="featured" className="text-sm text-[#1A1A1A]/70">
                        Featured Project
                      </label>
                    </div>
                  </>
                )}

                {/* Service Form */}
                {activeTab === "services" && editingItem && (
                  <>
                    <div>
                      <label className="block text-xs uppercase tracking-wider text-[#1A1A1A]/60 mb-2">
                        Title
                      </label>
                      <input
                        type="text"
                        value={editingItem.title}
                        onChange={(e) =>
                          setEditingItem({ ...editingItem, title: e.target.value })
                        }
                        className="w-full border border-[#1A1A1A]/20 px-3 py-2 focus:border-[#1A1A1A] focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-wider text-[#1A1A1A]/60 mb-2">
                        Description
                      </label>
                      <textarea
                        value={editingItem.description}
                        onChange={(e) =>
                          setEditingItem({ ...editingItem, description: e.target.value })
                        }
                        rows={3}
                        className="w-full border border-[#1A1A1A]/20 px-3 py-2 focus:border-[#1A1A1A] focus:outline-none resize-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-wider text-[#1A1A1A]/60 mb-2">
                        Icon
                      </label>
                      <select
                        value={editingItem.icon}
                        onChange={(e) =>
                          setEditingItem({ ...editingItem, icon: e.target.value })
                        }
                        className="w-full border border-[#1A1A1A]/20 px-3 py-2 focus:border-[#1A1A1A] focus:outline-none"
                      >
                        <option value="building">Building</option>
                        <option value="palette">Palette</option>
                        <option value="map">Map</option>
                        <option value="clipboard">Clipboard</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-wider text-[#1A1A1A]/60 mb-2">
                        Features (comma separated)
                      </label>
                      <input
                        type="text"
                        value={editingItem.features?.join(", ") || ""}
                        onChange={(e) =>
                          setEditingItem({
                            ...editingItem,
                            features: e.target.value.split(",").map((f) => f.trim()).filter(Boolean),
                          })
                        }
                        className="w-full border border-[#1A1A1A]/20 px-3 py-2 focus:border-[#1A1A1A] focus:outline-none"
                        placeholder="Feature 1, Feature 2, Feature 3"
                      />
                    </div>
                  </>
                )}

                {/* Team Form */}
                {activeTab === "team" && editingItem && (
                  <>
                    <div>
                      <label className="block text-xs uppercase tracking-wider text-[#1A1A1A]/60 mb-2">
                        Name
                      </label>
                      <input
                        type="text"
                        value={editingItem.name}
                        onChange={(e) =>
                          setEditingItem({ ...editingItem, name: e.target.value })
                        }
                        className="w-full border border-[#1A1A1A]/20 px-3 py-2 focus:border-[#1A1A1A] focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-wider text-[#1A1A1A]/60 mb-2">
                        Role
                      </label>
                      <input
                        type="text"
                        value={editingItem.role}
                        onChange={(e) =>
                          setEditingItem({ ...editingItem, role: e.target.value })
                        }
                        className="w-full border border-[#1A1A1A]/20 px-3 py-2 focus:border-[#1A1A1A] focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-wider text-[#1A1A1A]/60 mb-2">
                        Bio
                      </label>
                      <textarea
                        value={editingItem.bio}
                        onChange={(e) =>
                          setEditingItem({ ...editingItem, bio: e.target.value })
                        }
                        rows={3}
                        className="w-full border border-[#1A1A1A]/20 px-3 py-2 focus:border-[#1A1A1A] focus:outline-none resize-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-wider text-[#1A1A1A]/60 mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        value={editingItem.email || ""}
                        onChange={(e) =>
                          setEditingItem({ ...editingItem, email: e.target.value })
                        }
                        className="w-full border border-[#1A1A1A]/20 px-3 py-2 focus:border-[#1A1A1A] focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-wider text-[#1A1A1A]/60 mb-2">
                        Image URL
                      </label>
                      <input
                        type="text"
                        value={editingItem.image_url}
                        onChange={(e) =>
                          setEditingItem({ ...editingItem, image_url: e.target.value })
                        }
                        className="w-full border border-[#1A1A1A]/20 px-3 py-2 focus:border-[#1A1A1A] focus:outline-none"
                        placeholder="https://..."
                      />
                      {editingItem.image_url && (
                        <img
                          src={editingItem.image_url}
                          alt="Preview"
                          className="mt-2 w-20 h-20 object-cover rounded-full mx-auto"
                        />
                      )}
                    </div>
                  </>
                )}
              </div>

              <div className="flex justify-end gap-3 p-4 border-t border-[#1A1A1A]/10">
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 text-sm text-[#1A1A1A]/70 hover:text-[#1A1A1A]"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  data-testid="save-btn"
                  className="flex items-center gap-2 bg-[#1A1A1A] text-[#F4F4F0] px-4 py-2 text-sm hover:bg-[#3A4D39] transition-colors"
                >
                  <Save size={16} />
                  Save
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminDashboard;
